import {
  Box,
  Grid,
  Modal,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Button,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import SubHeader from "../components/SubHeader";
import { useLocation, useNavigate } from "react-router-dom";
import ModalAmlForm from "../components/Generics/ModalAmlForm";
import { validationCreateAml } from "../utils/validation_create_aml";
import PopupError from "../components/Generics/PopupError";
import AmlExpired from "../components/DashboardAdmin/AmlExpired";
import AmlPending from "../components/DashboardAdmin/AmlPending";
import AmlValid from "../components/DashboardAdmin/AmlValid";
import Searchbar from "../components/Searchbar";
import ModalLegendStatus from "../components/Generics/ModalLegendStatus";
import ModalSuccess from "../components/Generics/ModalSuccess";
import { service } from "../model";
import ModalError from "../components/Generics/ModalError";
import { useSelector } from "react-redux";
import BoxButton from "../components/BoxButton";
import { Pagination, PaginationItem } from "@mui/material";
import SearchAml from "../components/DashboardAdmin/SearchAml";

const ALL_STATUS_OPTIONS = [
  { value: "sent", label: "Spedito" },
  { value: "in_progress", label: "In compilazione" },
  { value: "submitted", label: "Da verificare" },
  { value: "rejected", label: "In correzione" },
  { value: "expired", label: "Scaduto" },
  { value: "approved", label: "Valido" },
  { value: "pending_confirmation", label: "In ratifica" },
  { value: "archived", label: "Archiviato" },
];

const VALID_STATUS_OPTIONS = [
  { value: "approved", label: "Valido" },
  { value: "pending_confirmation", label: "In ratifica" },
];

const ROUTE_DEFAULT_STATUS = {
  "/aml/moduli-scaduti": "expired",
  "/aml/moduli-in-raccolta": "",
  "/aml/moduli-validi": "",
  "/aml/ricerca-moduli": "",
};

const DashboardAdmin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reduxObject = useSelector((state) => state.user?.redux_object);
  const token = reduxObject?.accessToken;

  const IN_RACCOLTA_STATUSES = "sent,in_progress,submitted,rejected";
  const VALID_STATUSES = "approved,pending_confirmation";

  const [allAml, setAllAml] = useState([]);
  const [loadingAml, setLoadingAml] = useState(true);

  const [openModalNewAml, setOpenModalNewAml] = useState(false);
  const [openLegenda, setOpenLegenda] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const [apiErrorModal, setApiErrorModal] = useState(false);
  const [errorsCreateAml, setErrorsCreateAml] = useState([]);
  const [errorPopup, setErrorPopup] = useState(false);

  const [newAml, setNewAml] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const PER_PAGE = 20;

  const [statusCounts, setStatusCounts] = useState({
    expired: null,
    submitted: null,
    pending_confirmation: null,
  });

  const statusOptions =
    location.pathname === "/aml/moduli-in-raccolta"
      ? ALL_STATUS_OPTIONS.slice(0, 4)
      : location.pathname === "/aml/moduli-validi"
        ? VALID_STATUS_OPTIONS
        : ALL_STATUS_OPTIONS;

  useEffect(() => {
    if (errorPopup) {
      const timer = setTimeout(() => setErrorPopup(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorPopup]);

  const statusCountsFetchedRef = useRef(false);
  useEffect(() => {
    if (!token || statusCountsFetchedRef.current) return;
    statusCountsFetchedRef.current = true;
    const loadStatusCounts = async () => {
      try {
        const [expiredRes, submittedRes, ratificaRes] = await Promise.all([
          service.getAllAml(token, { page: 1, per_page: 1, status: "expired" }),
          service.getAllAml(token, { page: 1, per_page: 1, status: "submitted" }),
          service.SearchAml(token, {
            pattern: "",
            status: "pending_confirmation",
            per_page: 1,
            page: 1,
          }),
        ]);
        setStatusCounts({
          expired: expiredRes.total ?? 0,
          submitted: submittedRes.total ?? 0,
          pending_confirmation: ratificaRes.total ?? 0,
        });
      } catch (err) {
        console.error(err);
      }
    };
    loadStatusCounts();
  }, [token]);

  const loadAppData = async (page = 1, status = "") => {
    if (!token) return;
    try {
      setLoadingAml(true);
      const result = await service.getAllAml(token, {
        page,
        per_page: PER_PAGE,
        ...(status ? { status } : {}),
      });
      setAllAml(result.data || []);
      setLastPage(result.last_page || 1);
      setCurrentPage(result.current_page || 1);
    } catch (err) {
      console.error(err);
      setApiErrorModal(true);
    } finally {
      setLoadingAml(false);
    }
  };

  useEffect(() => {
    setSearchTerm("");
    setFilterStatus("");
    const defaultStatus = ROUTE_DEFAULT_STATUS[location.pathname] ?? "";

    if (location.pathname === "/aml/ricerca-moduli") {
      setAllAml([]);
      setLastPage(1);
      setCurrentPage(1);
      setLoadingAml(false);
    } else if (location.pathname === "/aml/moduli-in-raccolta") {
      loadAppData(1, IN_RACCOLTA_STATUSES);
    } else if (location.pathname === "/aml/moduli-validi") {
      setFilterStatus("pending_confirmation");
      handleSearch("", "pending_confirmation", 1);
    } else if (defaultStatus) {
      setFilterStatus(defaultStatus);
      handleSearch("", defaultStatus, 1);
    } else {
      loadAppData(1);
    }
  }, [token, location.pathname]);

  const handleSearch = async (term, status = filterStatus, page = 1) => {
    setSearchTerm(term);
    if (!term && !status) {
      if (location.pathname === "/aml/ricerca-moduli") {
        setAllAml([]);
        setLastPage(1);
        setCurrentPage(1);
        return;
      }
      const defaultStatus =
        location.pathname === "/aml/moduli-in-raccolta" ? IN_RACCOLTA_STATUSES :
        location.pathname === "/aml/moduli-validi" ? "pending_confirmation" : "";
      await loadAppData(1, defaultStatus);
      return;
    }
    try {
      setLoadingAml(true);
      const result = await service.SearchAml(token, {
        pattern: term,
        status: status,
        // status: "sent,in_progress,submitted,rejected",
        per_page: PER_PAGE,
        page,
      });
      setAllAml(result.data || []);
      setLastPage(result.last_page || 1);
      setCurrentPage(result.current_page || 1);
    } catch (err) {
      console.error(err);
      setApiErrorModal(true);
    } finally {
      setLoadingAml(false);
    }
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setFilterStatus(val);
    handleSearch(searchTerm, val);
  };

  const handleCloseModalNewAml = () => {
    setOpenModalNewAml(false);
    setNewAml({});
  };

  const handleConfirmCreateAml = async () => {
    const validationErrors = validationCreateAml(newAml);
    if (validationErrors.length > 0) {
      setErrorsCreateAml(validationErrors);
      setErrorPopup(true);
      return;
    }
    try {
      setLoadingAml(true);
      await service.startAML(token, newAml);
      setErrorsCreateAml([]);
      handleCloseModalNewAml();
      setOpenSuccessModal(true);
      await loadAppData();
    } catch (error) {
      console.error(error);
      setApiErrorModal(true);
    } finally {
      setLoadingAml(false);
    }
  };

  const handleResetFilters = async () => {
    setSearchTerm("");
    const defaultStatus = ROUTE_DEFAULT_STATUS[location.pathname] ?? "";
    setFilterStatus(defaultStatus);

    if (location.pathname === "/aml/ricerca-moduli") {
      setAllAml([]);
      setLastPage(1);
      setCurrentPage(1);
    } else if (location.pathname === "/aml/moduli-in-raccolta") {
      await loadAppData(1, IN_RACCOLTA_STATUSES);
    } else if (location.pathname === "/aml/moduli-validi") {
      setFilterStatus("pending_confirmation");
      await handleSearch("", "pending_confirmation", 1);
    } else if (defaultStatus) {
      await handleSearch("", defaultStatus, 1);
    } else {
      await loadAppData(1);
    }
  };

  return (
    <>
      {loadingAml && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress sx={{ color: "#1b2838" }} />
        </div>
      )}

      <div
        style={{ display: "flex", flexDirection: "column", height: "100vh" }}
      >
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
          <Header />
        </div>

        <Grid
          container
          justifyContent="center"
          sx={{ mt: "140px", px: 2, pb: "80px" }}
        >
          <Grid item xs={12} md={10}>
            {/* ── TITOLO + SELECT NAVIGAZIONE ── */}
            <Grid container alignItems="center" sx={{ mb: 3 }} spacing={2}>
              <Grid item>
                <Typography
                  className="color-blue-dark"
                  sx={{ fontSize: 22, fontWeight: 600 }}
                >
                  {location.pathname === "/aml/moduli-in-raccolta" &&
                    "Moduli AML in raccolta"}
                  {location.pathname === "/aml/moduli-scaduti" &&
                    "Moduli AML scaduti"}
                  {location.pathname === "/aml/moduli-validi" &&
                    "Moduli AML validi"}
                  {location.pathname === "/aml/ricerca-moduli" && "Ricerca AML"}
                </Typography>
              </Grid>
              <Grid item sx={{ marginLeft: { xs: "0px", md: "30px" } }}>
                <Select
                  value={
                    [
                      "/aml/moduli-in-raccolta",
                      "/aml/moduli-scaduti",
                      "/aml/moduli-validi",
                      "/aml/ricerca-moduli",
                    ].includes(location.pathname)
                      ? location.pathname
                      : "/aml/moduli-in-raccolta"
                  }
                  onChange={(e) => navigate(e.target.value)}
                  size="small"
                  sx={{
                    backgroundColor: "#fff",
                    fontSize: { xs: "13px", sm: "15px" },
                    height: "40px",
                    borderRadius: "10px",
                    minWidth: 180,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "10px",
                    },
                  }}
                >
                  <MenuItem value="/aml/moduli-in-raccolta">
                    Moduli in raccolta
                    {statusCounts.submitted > 0 && (
                      <span style={{ color: "#d32f2f" }}>
                        {` (${statusCounts.submitted})`}
                      </span>
                    )}
                  </MenuItem>
                  <MenuItem value="/aml/moduli-scaduti">
                    Moduli scaduti
                    {statusCounts.expired > 0 && (
                      <span style={{ color: "#d32f2f" }}>
                        {` (${statusCounts.expired})`}
                      </span>
                    )}
                  </MenuItem>
                  <MenuItem value="/aml/moduli-validi">
                    Moduli validi
                    {statusCounts.pending_confirmation > 0 && (
                      <span style={{ color: "#d32f2f" }}>
                        {` (${statusCounts.pending_confirmation})`}
                      </span>
                    )}
                  </MenuItem>
                  <MenuItem value="/aml/ricerca-moduli">
                    Ricerca moduli
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>

            <Grid container sx={{ mb: 3 }} alignItems="center" spacing={2}>
              <Grid item xs={12} sm={5}>
                <Searchbar
                  onSearch={(term) => handleSearch(term)}
                  initialValue={searchTerm}
                />
              </Grid>

              {(location.pathname === "/aml/moduli-in-raccolta" ||
                location.pathname === "/aml/moduli-validi" ||
                location.pathname === "/aml/ricerca-moduli") && (
                  <Grid item xs={12} sm={3}>
                    <Select
                      value={filterStatus}
                      onChange={handleStatusChange}
                      displayEmpty
                      fullWidth
                      sx={{ height: "56px", borderRadius: "10px" }}
                    >
                      <MenuItem value="">
                        <span style={{ color: "#abb0b6" }}>Filtra per stato</span>
                      </MenuItem>
                      {statusOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          <Typography
                            className="color-blue-dark"
                            sx={{ fontSize: { xs: "14px", md: "16px" } }}
                          >
                            {s.label}
                            {s.value === "submitted" &&
                              statusCounts.submitted > 0 && (
                                <span style={{ color: "#d32f2f" }}>
                                  {` (${statusCounts.submitted})`}
                                </span>
                              )}
                            {s.value === "pending_confirmation" &&
                              statusCounts.pending_confirmation > 0 && (
                                <span style={{ color: "#d32f2f" }}>
                                  {` (${statusCounts.pending_confirmation})`}
                                </span>
                              )}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}

              <Grid
                item
                xs={12}
                sm={1.5}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-end", sm: "start" },
                  alignItems: "center",
                  ml: "10px",
                  mt: { xs: "0px", sm: "0px" },
                }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    height: "30px",
                    width: "60px",
                    borderRadius: "10px",
                    borderColor: "#c4c4c4",
                    textTransform: "initial",
                    "&:hover": {
                      borderColor: "#1b2838",
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                  onClick={handleResetFilters}
                >
                  <Box
                    className="icon-filter-reset-button"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </Button>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              {location.pathname === "/aml/moduli-in-raccolta" && (
                <AmlPending
                  allAml={allAml}
                  handleOpenLegendaModal={() => setOpenLegenda(true)}
                />
              )}

              {location.pathname === "/aml/moduli-scaduti" && (
                <AmlExpired
                  allAml={allAml}
                  handleOpenLegendaModal={() => setOpenLegenda(true)}
                />
              )}

              {location.pathname === "/aml/moduli-validi" && (
                <AmlValid
                  allAml={allAml}
                  handleOpenLegendaModal={() => setOpenLegenda(true)}
                />
              )}

              {location.pathname === "/aml/ricerca-moduli" &&
                allAml.length > 0 && (
                  <SearchAml
                    allAml={allAml}
                    handleOpenLegendaModal={() => setOpenLegenda(true)}
                  />
                )}

              {lastPage > 1 && !loadingAml && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center", mb: 3 }}
                >
                  <Pagination
                    count={lastPage}
                    page={currentPage}
                    onChange={(_, page) => {
                      if (searchTerm || filterStatus) {
                        handleSearch(searchTerm, filterStatus, page);
                      } else {
                        const status =
                          location.pathname === "/aml/moduli-in-raccolta"
                            ? IN_RACCOLTA_STATUSES
                            : location.pathname === "/aml/moduli-validi"
                            ? "pending_confirmation"
                            : "";
                        loadAppData(page, status);
                      }
                    }}
                    shape="rounded"
                    renderItem={(item) => (
                      <PaginationItem
                        components={{
                          previous: () => (
                            <Box className="icon-arrow-pagination" />
                          ),
                          next: () => (
                            <Box className="icon-arrow-pagination right" />
                          ),
                        }}
                        {...item}
                      />
                    )}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#1b2838",
                        borderColor: "#1b2838",
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#1b2838",
                        color: "#fff",
                        "&:hover": { opacity: 0.8 },
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <BoxButton open={setOpenModalNewAml} />
        </Grid>
      </div>

      {/* CREATE MODAL */}
      <Modal
        open={openModalNewAml}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCloseModalNewAml();
        }}
      >
        <Box>
          <ModalAmlForm
            mode="create"
            aml={newAml}
            setAml={setNewAml}
            errors={errorsCreateAml}
            confirm={handleConfirmCreateAml}
            onClose={handleCloseModalNewAml}
            errorPopup={errorPopup}
            setErrorPopup={setErrorPopup}
          />
        </Box>
      </Modal>

      {/* SUCCESS */}
      <Modal open={openSuccessModal} onClose={() => setOpenSuccessModal(false)}>
        <div>
          <ModalSuccess
            title="Modulo AML creato con successo"
            descriptionOne="Il link per la compilazione è stato inviato al cliente."
            descriptionTwo=""
            copyButton="Chiudi"
            confirm={() => setOpenSuccessModal(false)}
            onClose={() => setOpenSuccessModal(false)}
          />
        </div>
      </Modal>

      {openLegenda && (
        <ModalLegendStatus
          title="Legenda degli stati dei modelli AML in raccolta"
          onClose={() => setOpenLegenda(false)}
        />
      )}

      <Modal open={apiErrorModal} onClose={() => setApiErrorModal(false)}>
        <div>
          <ModalError
            title="Errore"
            descriptionOne="Si è verificato un errore tecnico momentaneo."
            descriptionTwo="Ti invitiamo a riprovare tra qualche minuto."
            copyButton="Chiudi"
            confirm={() => setApiErrorModal(false)}
            onClose={() => setApiErrorModal(false)}
            generalError
          />
        </div>
      </Modal>
    </>
  );
};

export default DashboardAdmin;
